import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck, FileText, Save } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { resyncCatalog, getProperties, getAdminDescription, saveAdminDescription } from "@/lib/api";

const LANGS = [
  { code: "it", name: "Italiano" },
  { code: "en", name: "English" },
  { code: "hr", name: "Hrvatski" },
  { code: "sl", name: "Slovenščina" },
  { code: "de", name: "Deutsch" },
  { code: "hu", name: "Magyar" },
  { code: "cs", name: "Čeština" },
];

export default function AdminSync() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Description editor state
  const [propList, setPropList] = useState([]);
  const [slug, setSlug] = useState("");
  const [descText, setDescText] = useState("");
  const [descLang, setDescLang] = useState("it");
  const [source, setSource] = useState("");
  const [hasOverride, setHasOverride] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);

  useEffect(() => {
    setToken(localStorage.getItem("ubp_admin_token") || "");
    getProperties({ limit: 100 })
      .then((items) => setPropList((items || []).map((p) => ({ slug: p.slug, name: p.name }))))
      .catch(() => {});
  }, []);

  const run = async () => {
    if (!token) return;
    setLoading(true);
    setResult(null);
    try {
      localStorage.setItem("ubp_admin_token", token);
      const data = await resyncCatalog(token);
      setResult(data);
      toast.success(`Catalogo aggiornato: ${data.count} strutture reali da Booking.com`);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 401
          ? "Token non valido"
          : status === 503
          ? "Booking non disponibile, riprova tra poco"
          : "Errore durante la sincronizzazione"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadDescription = useCallback(async (s) => {
    if (!token || !s) return;
    setLoadingDesc(true);
    try {
      localStorage.setItem("ubp_admin_token", token);
      const data = await getAdminDescription(token, s);
      setSource(data.source_preview || "");
      setHasOverride(!!data.override);
      setDescText(data.override?.text || "");
      setDescLang(data.override?.lang || "it");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Token non valido" : "Errore nel caricamento");
    } finally {
      setLoadingDesc(false);
    }
  }, [token]);

  const onSelectProperty = (s) => {
    setSlug(s);
    setDescText("");
    setSource("");
    setHasOverride(false);
    if (s) loadDescription(s);
  };

  const saveDescription = async () => {
    if (!token || !slug) return;
    setSavingDesc(true);
    try {
      const data = await saveAdminDescription(token, slug, descText, descLang);
      setHasOverride(data.status === "saved");
      toast.success(data.status === "saved" ? "Descrizione salvata (tradotta in automatico)" : "Descrizione ripristinata da Booking");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Token non valido" : "Errore nel salvataggio");
    } finally {
      setSavingDesc(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <Navbar solid />
      <div className="mx-auto max-w-2xl px-5 py-24">
        {/* Shared admin token */}
        <label className="block text-xs font-semibold uppercase tracking-wide text-petrol/60">Token admin</label>
        <input
          data-testid="admin-token-input"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Inserisci il token admin"
          className="mt-2 w-full border border-sand-border bg-white px-4 py-3 text-sm text-petrol outline-none focus:border-terracotta"
        />

        {/* Resync card */}
        <div className="mt-8 border border-sand-border bg-sand-card p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center bg-petrol text-sand">
              <ShieldCheck size={20} />
            </span>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-petrol">Sincronizza catalogo</h1>
              <p className="text-sm text-petrol/60">Ricarica gli hotel reali di Umago da Booking.com</p>
            </div>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-petrol/70">
            {"Aggiorna nomi, foto, punteggi e prezzi con gli ultimi dati Booking.com. Operazione sicura: aggiorna le strutture esistenti senza cancellare dati (prenotazioni e avvisi restano intatti)."}
          </p>

          <button
            data-testid="admin-resync-button"
            onClick={run}
            disabled={loading || !token}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-none bg-terracotta px-6 py-3.5 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            {loading ? "Sincronizzazione in corso…" : "Risincronizza ora"}
          </button>

          {result && (
            <div data-testid="admin-resync-result" className="mt-5 border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              ✓ {result.count} strutture reali sincronizzate da Booking.com
            </div>
          )}
        </div>

        {/* Description editor card */}
        <div className="mt-8 border border-sand-border bg-sand-card p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center bg-petrol text-sand">
              <FileText size={20} />
            </span>
            <div>
              <h2 className="font-serif text-2xl font-semibold text-petrol">Descrizioni struttura</h2>
              <p className="text-sm text-petrol/60">Scrivi una descrizione originale (tradotta in automatico nelle altre lingue)</p>
            </div>
          </div>

          <label className="mt-6 block text-xs font-semibold uppercase tracking-wide text-petrol/60">Struttura</label>
          <select
            data-testid="admin-desc-property"
            value={slug}
            onChange={(e) => onSelectProperty(e.target.value)}
            className="mt-2 w-full border border-sand-border bg-white px-4 py-3 text-sm text-petrol outline-none focus:border-terracotta"
          >
            <option value="">— Seleziona una struttura —</option>
            {propList.map((p) => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
          </select>

          {loadingDesc && (
            <p className="mt-4 flex items-center gap-2 text-sm text-petrol/60"><Loader2 size={15} className="animate-spin" /> Caricamento…</p>
          )}

          {slug && !loadingDesc && (
            <>
              <div className="mt-5 flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wide text-petrol/60">Descrizione originale</label>
                <select
                  data-testid="admin-desc-lang"
                  value={descLang}
                  onChange={(e) => setDescLang(e.target.value)}
                  className="border border-sand-border bg-white px-2 py-1 text-xs text-petrol outline-none focus:border-terracotta"
                >
                  {LANGS.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="admin-desc-textarea"
                value={descText}
                onChange={(e) => setDescText(e.target.value)}
                rows={7}
                placeholder="Lascia vuoto per usare la descrizione reale di Booking.com"
                className="mt-2 w-full border border-sand-border bg-white px-4 py-3 text-sm leading-relaxed text-petrol outline-none focus:border-terracotta"
              />
              <p className="mt-1 text-xs text-petrol/50">
                {hasOverride ? "Descrizione personalizzata attiva." : "Nessuna personalizzazione: viene usata Booking.com."}
              </p>

              <button
                data-testid="admin-desc-save"
                onClick={saveDescription}
                disabled={savingDesc}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-none bg-petrol px-6 py-3 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
              >
                {savingDesc ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {descText.trim() ? "Salva descrizione" : "Ripristina da Booking"}
              </button>

              {source && (
                <details className="mt-5 text-sm text-petrol/70">
                  <summary className="cursor-pointer font-semibold text-petrol/80">Testo originale di Booking.com</summary>
                  <p className="mt-2 whitespace-pre-line border-l-2 border-sand-border pl-3 text-petrol/60">{source}</p>
                </details>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
